import { useMemo } from 'react';
import { useTranslation } from '@/i18n/useTranslation';
import type { Translation } from '@/i18n/translations';
import type { WizardAnswers } from '@/context/WizardSessionContext';
import { buildWizardReviewParcelRows } from '@/lib/wizard-review-display';
import { useLocale } from '@/i18n/LocaleProvider';
import styles from './ReviewStep.module.css';

export interface ReviewStepProps {
  answers: WizardAnswers;
}

function labelForVenture(t: Translation, value: string | undefined): string {
  if (!value) return '—';
  const key = value as keyof typeof t.wizard.ventureTypes;
  return t.wizard.ventureTypes[key] ?? value;
}

function labelForTimeline(t: Translation, value: string | undefined): string {
  if (!value) return '—';
  const key = value as keyof typeof t.wizard.timelines;
  return t.wizard.timelines[key] ?? value;
}

function SummaryRow({
  term,
  value,
  ltr,
}: {
  term: string;
  value: string;
  /** Latin / numeric text — keep RTL row alignment, render digits LTR. */
  ltr?: boolean;
}) {
  return (
    <div className={styles.row}>
      <dt className={styles.term}>{term}</dt>
      <dd className={styles.value}>
        {ltr ? (
          <span className={styles.ltrInline} dir="ltr">
            {value}
          </span>
        ) : (
          value
        )}
      </dd>
    </div>
  );
}

export default function ReviewStep({ answers }: ReviewStepProps) {
  const t = useTranslation();
  const { language } = useLocale();

  const parcelRows = useMemo(
    () =>
      buildWizardReviewParcelRows(
        answers,
        {
          designation: t.map.fields.designation,
          planPrimary: t.map.fields.planPrimary,
          migrash: t.wizard.fields.migrash,
          cadastral: t.map.fields.cadastralRef,
          mapLocation: t.wizard.fields.mapLocation,
          drawnArea: t.map.drawnAreaLabel,
        },
        language,
      ),
    [answers, language, t],
  );

  return (
    <section data-testid="review-step" className={styles.review}>
      <p className={styles.intro}>{t.wizard.reviewIntro}</p>

      <h3 className={styles.sectionTitle}>{t.wizard.reviewVentureSection}</h3>
      <dl className={styles.summary}>
        <SummaryRow
          term={t.wizard.fields.ventureType}
          value={labelForVenture(t, answers.venture_type)}
        />
        <SummaryRow
          term={t.wizard.fields.requiredArea}
          value={
            answers.required_area_sqm != null
              ? `${answers.required_area_sqm} ${t.wizard.units.sqm}`
              : '—'
          }
        />
        <SummaryRow
          term={t.wizard.fields.timeline}
          value={labelForTimeline(t, answers.timeline)}
        />
      </dl>

      {parcelRows.length > 0 ? (
        <>
          <h3 className={styles.sectionTitle}>{t.wizard.fields.parcelSection}</h3>
          <dl className={styles.summary}>
            {parcelRows.map((row) => (
              <SummaryRow
                key={row.label}
                term={row.label}
                value={row.value}
                ltr={row.dir === 'ltr'}
              />
            ))}
          </dl>
        </>
      ) : null}

      <h3 className={styles.sectionTitle}>{t.wizard.reviewContactSection}</h3>
      <dl className={styles.summary}>
        <SummaryRow
          term={t.wizard.fields.contactName}
          value={answers.contact_name || '—'}
        />
        <SummaryRow
          term={t.wizard.fields.contactPhone}
          value={answers.contact_phone || '—'}
          ltr
        />
        <SummaryRow
          term={t.wizard.fields.contactEmail}
          value={answers.contact_email || '—'}
          ltr
        />
      </dl>
    </section>
  );
}
